import { useEffect, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import Home from "@/pages/Home";
import Product25D from "@/pages/Product25D";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";
import Product3D from "@/pages/Product3D";
import Work from "@/pages/Work";
import Specifications from "@/pages/Specifications";
import ProductDozer from "@/pages/ProductDozer";
import ProductRover from "@/pages/ProductRover";
import PrecisionSolutions from "@/pages/PrecisionSolutions";
import Compare from "@/pages/Compare";
import Hire from "@/pages/Hire";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-ink px-6 text-center">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-signal">System fault</p>
            <p className="mt-4 font-display text-3xl font-bold uppercase text-white">Something went wrong</p>
            <button
              onClick={() => window.location.reload()}
              data-testid="error-reload-btn"
              className="mt-8 border border-signal px-6 py-3 font-mono text-xs uppercase tracking-[0.25em] text-neon"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        const t = setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 350);
        return () => clearTimeout(t);
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
  return null;
}

const TITLES = {
  "/": "Green Grade — 2.5D & 3D Excavator Machine Control | CHCNAV",
  "/products/2.5d": "2.5D Excavator Machine Control — EasyNAV EMG100 | Green Grade",
  "/products/3d": "3D Excavator Machine Control — MCNAV TX73 | Green Grade",
  "/products/dozer": "3D Dozer Control — TD63 Pro | Green Grade",
  "/products/rover": "GNSS Survey Rover — i83 | Green Grade",
  "/precision-solutions": "CHCNAV Precision Solutions — The Global Leader | Green Grade",
  "/compare": "2D vs 2.5D vs 3D Excavator Systems — Compare | Green Grade",
  "/work": "Field Proof — Trench, Slope & Foundation Jobs | Green Grade",
  "/specifications": "Spec Sheets — EasyNAV EMG100 & MCNAV TX73 | Green Grade",
  "/contact": "Request a Demo — Excavator Machine Control | Green Grade",
  "/hire": "Hire 3D Machine Control — 3D On Demand | Green Grade",
};

const DESCRIPTIONS = {
  "/": "Green Grade supplies and installs CHCNAV 2.5D & 3D machine control for excavators and dozers. 10 mm GNSS accuracy, 2-hour retrofit, full training and local support. Request a demo today.",
  "/products/2.5d": "CHCNAV EasyNAV EMG100 2.5D excavator machine control — ±30 mm accuracy, dual GNSS, 30-minute calibration, installed in under 2 hours. Supplied and supported across Australia by Green Grade.",
  "/products/3d": "CHCNAV MCNAV TX73 3D excavator machine control — 10 mm horizontal accuracy, runs LandXML, DWG and DXF files, tilt-ready. Supplied and supported across Australia by Green Grade.",
  "/products/dozer": "CHCNAV TD63 Pro 3D dozer machine control — ±20 mm automatic blade control. Supplied, installed and supported across Australia by Green Grade.",
  "/products/rover": "CHCNAV i83 IMU-RTK GNSS survey rover — 1408 channels, 8 mm + 1 ppm RTK, calibration-free 60° tilt compensation, 34-hour battery and built-in UHF and 4G. Supplied across Australia by Green Grade.",
  "/precision-solutions": "CHCNAV — founded 2003, public since 2017, 2,200+ staff and 1,000+ R&D engineers across 140+ countries. Vertically integrated machine control, supplied and supported in Australia by Green Grade.",
  "/compare": "Choosing between 2D, 2.5D and 3D excavator machine control? Answer three questions, compare the full CHCNAV lineup side by side and see the upgrade paths. Green Grade, Australia.",
  "/work": "Field proof for CHCNAV machine control — trenching, slope and foundation jobs running on Green Grade installs across Australia.",
  "/specifications": "Full CHCNAV machine control specifications — EasyNAV EMG100, MCNAV TX73, TD63 Pro and i83. Download branded spec sheets from Green Grade.",
  "/contact": "Book a demo or build a quote — CHCNAV 2.5D and 3D machine control from Green Grade. Call 1300 277 689.",
  "/hire": "Hire CHCNAV 3D machine control by the month. We fit your excavator with receivers, antennas, sensors and screen — 2D for the life of the machine, full 3D on demand (1, 3 or 6-month access). Green Grade, Australia.",
};

function SeoManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    const title = TITLES[pathname] || TITLES["/"];
    const description = DESCRIPTIONS[pathname] || DESCRIPTIONS["/"];
    document.title = title;
    const descTag = document.querySelector('meta[name="description"]');
    if (descTag) descTag.setAttribute("content", description);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", description);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute("content", `${window.location.origin}${pathname === "/" ? "/" : pathname}`);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${window.location.origin}${pathname === "/" ? "/" : pathname}`);
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <SmoothScroll />
        <ScrollToTop />
        <SeoManager />
        <div className="noise-overlay" aria-hidden="true" />
        <Nav />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/products/2d" element={<Navigate to="/hire" replace />} />
          <Route path="/products/2.5d" element={<Product25D />} />
          <Route path="/products/3d" element={<Product3D />} />
          <Route path="/products/dozer" element={<ProductDozer />} />
          <Route path="/products/rover" element={<ProductRover />} />
          <Route path="/precision-solutions" element={<PrecisionSolutions />} />
          <Route path="/work" element={<Work />} />
          <Route path="/specifications" element={<Specifications />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/hire" element={<Hire />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Footer />
        <WhatsAppButton />
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-center" />
    </ErrorBoundary>
  );
}
